/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  SxProps,
  Typography,
  TextField,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { currencyUtils } from 'skipify-types';
import { NumericFormat } from 'react-number-format';

import { PaylinkCustomerInfoResponse } from '../../../../store/modules/paylink/types';
import { useGetAllCardOnFileCustomersQuery } from '../../../../store/modules/merchant/api';
import { Button } from '../../../../components/core/Button/Button';
import { AcceptedCurrency } from '../../../../store/modules/merchant/types';
import { formatCurrency } from '../../../../util/formatters';
import { CardOnFileCell } from '../../../../common/Customers/CardOnFileCell';
import { sanitizeNumericString } from '../../../../util/helpers';
import { CreateTransactionArgs } from '../../../../hooks/useCreateTransaction';

const validationSchema = z.object({
  amount: z
    .string()
    .min(1, 'Required')
    .refine(
      (amount) => {
        const amountNumber = Number(sanitizeNumericString(amount as string));
        return !Number.isNaN(amountNumber) && amountNumber > 0;
      },
      { message: 'Invalid amount' },
    ),
  description: z.string().max(255, 'Description must be less than 255 characters').optional(),
  merchantReference: z.string().min(1, 'Required'),
  customerId: z.string().min(1, 'Required'),
});

type FormValues = z.infer<typeof validationSchema>;

interface Props {
  merchantId: string;
  onSubmit: (data: CreateTransactionArgs) => Promise<void>;
  preSelectedCustomer?: PaylinkCustomerInfoResponse;
  sx?: SxProps;
  currency?: AcceptedCurrency;
}

const getCustomerLabel = (option: PaylinkCustomerInfoResponse) =>
  `${option.firstName} ${option.lastName}`;

export const CreateTransactionForm = ({
  merchantId,
  onSubmit,
  sx,
  preSelectedCustomer,
  currency,
}: Props) => {
  const [selectedCustomer, setSelectedCustomer] = useState<PaylinkCustomerInfoResponse | null>(
    preSelectedCustomer ?? null,
  );
  const [customerChargeAgreement, setCustomerChargeAgreement] = useState(false);

  const {
    data: cardOnFileCustomers,
    isLoading: isLoadingCustomers,
    error: errorLoadingCustomers,
  } = useGetAllCardOnFileCustomersQuery(merchantId);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    reset,
  } = useForm<FormValues>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      amount: '',
      description: '',
      merchantReference: '',
      customerId: preSelectedCustomer?.payLinkCustomerId ?? '',
    },
    reValidateMode: 'onBlur',
  });

  const onFormSubmit = async (data: FormValues) => {
    await onSubmit({
      customerId: data.customerId,
      amount: Number(sanitizeNumericString(data.amount)),
      merchantReference: data.merchantReference,
      description: data.description,
      customerChargeAgreement,
    });
    setSelectedCustomer(null);
    reset({
      amount: '',
      description: '',
      merchantReference: '',
      customerId: '',
    });
    setCustomerChargeAgreement(false);
  };

  const amount = watch('amount');
  const currencySymbol = currency?.symbol || '$';
  const parsedAmount = currencyUtils.minorToMajor(Number(sanitizeNumericString(amount)));

  return (
    <Stack
      spacing={2}
      component="form"
      noValidate
      onSubmit={handleSubmit(onFormSubmit)}
      id="create-transaction"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        ...sx,
      }}
    >
      <Controller
        name="customerId"
        control={control}
        render={({ field: { onChange, ...rest } }) => (
          <Autocomplete
            {...rest}
            options={cardOnFileCustomers ?? []}
            getOptionLabel={getCustomerLabel}
            value={selectedCustomer}
            onChange={(_, value) => {
              setSelectedCustomer(value);
              onChange(value?.payLinkCustomerId);
            }}
            isOptionEqualToValue={(customer, value) =>
              customer.payLinkCustomerId === value.payLinkCustomerId
            }
            renderInput={(params) => {
              return (
                <TextField
                  {...params}
                  required
                  label="Customer"
                  error={!!errors.customerId}
                  helperText={errors.customerId?.message}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {selectedCustomer && (
                          <CardOnFileCell
                            networkType={selectedCustomer?.networkType}
                            lastFour={selectedCustomer?.lastFour}
                            variant="compact"
                          />
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              );
            }}
            renderOption={(props, customer) => (
              <MenuItem {...props} key={customer.payLinkCustomerId}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                  }}
                >
                  <Typography>{getCustomerLabel(customer)}</Typography>
                  <CardOnFileCell
                    networkType={customer.networkType}
                    lastFour={customer.lastFour}
                    variant="compact"
                  />
                </Box>
              </MenuItem>
            )}
            noOptionsText={
              errorLoadingCustomers
                ? 'Error loading customers. Try again later.'
                : 'No customers found'
            }
            loading={isLoadingCustomers}
            loadingText="Loading customers..."
            slotProps={{
              paper: {
                variant: 'outlined',
                sx: { borderColor: 'grey.400', fontSize: 16 },
              },
            }}
            ListboxProps={{ sx: { maxHeight: '250px' } }}
          />
        )}
      />
      <Controller
        name="amount"
        control={control}
        render={({ field }) => (
          <NumericFormat
            customInput={TextField}
            variant="outlined"
            label="Amount"
            required
            thousandSeparator
            prefix={currencySymbol}
            fixedDecimalScale
            decimalScale={2}
            allowNegative={false}
            error={!!errors.amount}
            helperText={errors.amount?.message}
            onChange={field.onChange}
            value={field.value}
            name={field.name}
            id={field.name}
          />
        )}
      />
      <TextField
        label="Description"
        {...register('description')}
        id="description"
        multiline
        rows={2}
        inputProps={{ maxLength: 255 }}
        error={!!errors.description}
        helperText={errors.description?.message}
      />
      <TextField
        label="Merchant Reference"
        {...register('merchantReference')}
        id="merchantReference"
        required
        error={!!errors.merchantReference}
        helperText={errors.merchantReference?.message}
      />
      <FormControlLabel
        label="You confirm this customer has agreed in writing to charge their card for this purchase"
        control={
          <Checkbox
            required
            color="link"
            inputProps={{
              'aria-labelledby': 'customer-charge-agreement',
              'aria-required': 'true',
            }}
            checked={customerChargeAgreement}
            onChange={() => setCustomerChargeAgreement((prev) => !prev)}
          />
        }
        id="customer-charge-agreement"
        sx={{}}
        slotProps={{
          typography: {
            sx: {
              fontSize: 14,

              color: 'grey.700',
            },
          },
        }}
      />
      <Button
        text={`Charge customer${parsedAmount > 0 ? ` - ${formatCurrency(parsedAmount, currency?.code)}` : ''}`}
        isFullWidth
        type="submit"
        form="create-transaction"
        disabled={!customerChargeAgreement || isSubmitting}
        loading={isSubmitting}
      />
    </Stack>
  );
};
